'use strict';

const libPictView = require('pict-view');
const libDescriptors = require('../EntityDescriptors.js');
const libMarkdownRender = require('./MarkdownRender.js');

/**
 * TestRunner — the test run detail page (route /test-run/:id). Not the generic
 * EntityDetail: a run is a focused walk through snapshot steps, so the page is
 * a runner. Header shows the run, its routine, environment, and progress; each
 * step card shows the snapshot title, the routine step's body and reference
 * media (read from the live RoutineStep by IDRoutineStep provenance), the
 * expected result, big Worked / Didn't work / Skip buttons, tester notes, and
 * an evidence strip (Media OwnerType 'TestRunStep', click or drop to upload).
 *
 * Outcomes save optimistically (paint first, PUT after, revert on failure).
 * Finishing summarizes the outcomes in a modal, suggests Passed/Failed, stamps
 * Status + CompletedDate, then offers "Leave feedback" which opens the
 * Feedback form prelinked to this run. A finished run renders read-only
 * outcome chips. Comments mount at the bottom (CommentThread OwnerType
 * 'TestRun'), the same shared section the detail pages use.
 */
class PictViewTestRunner extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._RunID = 0;
		this._Run = null;
		this._Routine = null;
		this._LoadToken = 0;
		this._steps = [];        // TestRunStep rows, sorted by SortOrder
		this._routineSteps = {}; // IDRoutineStep -> RoutineStep row
		this._refMedia = {};     // IDRoutineStep -> Media rows (reference)
		this._evidence = {};     // IDTestRunStep -> Media rows (evidence)
		this._CommentsLoadedFor = null;
		this.pict.AppData.TestRunner = this.pict.AppData.TestRunner ||
			{
				RunID: 0, HeaderTitle: '', RoutineName: '', IDRoutine: 0, Environment: '', EnvironmentSlot: [],
				Status: '', StatusClass: '', Steps: [], Progress: { Done: 0, Total: 0, Pct: 0 },
				ActiveBarSlot: [], DoneBarSlot: [], SummarySlot: []
			};
	}

	_runAPI() { return this.pict.providers.TestRunAPI; }
	_stepAPI() { return this.pict.providers.TestRunStepAPI; }
	_routineStepAPI() { return this.pict.providers.RoutineStepAPI; }
	_mediaAPI() { return this.pict.providers.MediaAPI; }

	_active() { return !!(this._Run && this._Run.Status === 'In Progress'); }

	// ── entry + load ─────────────────────────────────────────────────────────
	showRun(pID)
	{
		this._RunID = Number(pID) || 0;
		this._CommentsLoadedFor = null;
		this._load();
	}

	_load()
	{
		let tmpToken = ++this._LoadToken;
		if (!this._RunID || !this._runAPI()) { return; }
		// Read the open Run by id, not from the capped TestRun list: a run past /0/250 is absent from that cache
		// and the runner would render nothing (WI #333, same class as #329). Fall back to the cache if the
		// read-one is momentarily unavailable. The run's step list still loads alongside it.
		Promise.all([this._runAPI().read(this._RunID), this._stepAPI().listForRun(this._RunID)])
			.then((pResults) =>
			{
				if (tmpToken !== this._LoadToken) { return null; }
				this._Run = pResults[0] || this._runAPI().store().Index[this._RunID] || null;
				this._steps = pResults[1] || [];
				if (!this._Run) { return null; }
				// The Routine, likewise by id rather than from the capped Routine list (WI #333).
				let tmpLoads = [this.pict.providers.RoutineAPI.read(this._Run.IDRoutine).then((pRoutine) =>
				{
					this._Routine = pRoutine || this.pict.providers.RoutineAPI.store().Index[this._Run.IDRoutine] || null;
				})];
				tmpLoads.push(this._routineStepAPI().listForRoutine(this._Run.IDRoutine).then((pRows) =>
				{
					this._routineSteps = {};
					(pRows || []).forEach((pRow) => { this._routineSteps[pRow.IDRoutineStep] = pRow; });
				}));
				this._steps.forEach((pStep) =>
				{
					tmpLoads.push(this._mediaAPI().listForOwner('RoutineStep', pStep.IDRoutineStep)
						.then((pRows) => { this._refMedia[pStep.IDRoutineStep] = pRows || []; }));
					tmpLoads.push(this._mediaAPI().listForOwner('TestRunStep', pStep.IDTestRunStep)
						.then((pRows) => { this._evidence[pStep.IDTestRunStep] = pRows || []; }));
				});
				return Promise.all(tmpLoads);
			})
			.then(() =>
			{
				if (tmpToken !== this._LoadToken) { return; }
				this._rebuild();
				this.render();
			});
	}

	_loadEvidenceFor(pID)
	{
		this._mediaAPI().listForOwner('TestRunStep', pID)
			.then((pRows) => { this._evidence[pID] = pRows || []; this._rebuild(); this.render(); });
	}

	_rawStep(pID) { return this._steps.find((pStep) => String(pStep.IDTestRunStep) === String(pID)) || null; }

	// ── build the AppData model from the raw caches (no fetch) ──────────────
	_rebuild()
	{
		let tmpState = this.pict.AppData.TestRunner;
		let tmpRun = this._Run || {};
		let tmpActive = this._active();
		tmpState.RunID = this._RunID;
		tmpState.HeaderTitle = (tmpRun.TestRunNumber ? ('Run #' + tmpRun.TestRunNumber + ': ') : '') + (tmpRun.Name || ('#' + this._RunID));
		// Name the browser tab + notebook history after this run once it has loaded (the route has no
		// static title, so it would otherwise linger on the generic default).
		if (this.pict.PictApplication && typeof this.pict.PictApplication._setPageTitle === 'function') { this.pict.PictApplication._setPageTitle(tmpState.HeaderTitle); }
		tmpState.RoutineName = (this._Routine && this._Routine.Name) || ('Routine #' + (tmpRun.IDRoutine || '?'));
		tmpState.IDRoutine = tmpRun.IDRoutine || 0;
		tmpState.Environment = tmpRun.Environment || '';
		tmpState.EnvironmentSlot = tmpRun.Environment ? [{ Environment: tmpRun.Environment }] : [];
		tmpState.Status = tmpRun.Status || '';
		tmpState.StatusChipClass = (' tsr-chip-' + String(tmpRun.Status || '').toLowerCase().replace(/[^a-z]+/g, '-'));

		let tmpDone = 0;
		tmpState.Steps = this._steps.map((pStep, pIdx) =>
		{
			let tmpID = pStep.IDTestRunStep;
			let tmpOutcome = String(pStep.Outcome || '');
			if (tmpOutcome) { tmpDone++; }
			let tmpRoutineStep = this._routineSteps[pStep.IDRoutineStep] || {};
			let tmpExpected = String(tmpRoutineStep.ExpectedResult || '');
			let tmpNotes = String(pStep.Notes || '');
			let fMediaItems = (pRows, pRemovable) => (pRows || []).map((pM) =>
			{
				let tmpIsImage = (String(pM.MimeType || '').indexOf('image/') === 0) || (pM.MediaType === 'Screenshot');
				let tmpIsVideo = String(pM.MimeType || '').indexOf('video/') === 0;
				let tmpURL = '/1.0/Media/' + pM.IDMedia + '/Blob';
				let tmpTitle = pM.Title || pM.OriginalFileName || ('Media #' + pM.IDMedia);
				return (
				{
					ID: pM.IDMedia, StepID: tmpID,
					ImageSlot: tmpIsImage ? [{ URL: tmpURL, Title: tmpTitle }] : [],
					VideoSlot: tmpIsVideo ? [{ URL: tmpURL, Title: tmpTitle }] : [],
					FileSlot: (tmpIsImage || tmpIsVideo) ? [] : [{ URL: tmpURL, Title: tmpTitle, Type: pM.MediaType || 'file' }],
					RemoveSlot: (pRemovable && tmpActive) ? [{ ID: pM.IDMedia, StepID: tmpID }] : []
				});
			});
			let tmpRefItems = fMediaItems(this._refMedia[pStep.IDRoutineStep], false);
			let tmpEvidenceItems = fMediaItems(this._evidence[tmpID], true);
			return (
			{
				ID: tmpID,
				Idx: pIdx + 1,
				Title: String(pStep.StepTitle || ''),
				Body: String(tmpRoutineStep.Body || ''),
				BodyContainerID: 'TR-Body-' + tmpID,
				ExpectedSlot: tmpExpected ? [{ Expected: tmpExpected }] : [],
				RefMedia: tmpRefItems,
				RefMediaSlot: tmpRefItems.length ? [{ Items: tmpRefItems }] : [],
				Evidence: tmpEvidenceItems,
				CardClass: tmpOutcome ? (' tsr-card-' + tmpOutcome.toLowerCase()) : '',
				OutcomeButtonsSlot: tmpActive ? [{
					ID: tmpID,
					PassedClass: (tmpOutcome === 'Passed') ? ' tsr-on' : '',
					FailedClass: (tmpOutcome === 'Failed') ? ' tsr-on' : '',
					SkippedClass: (tmpOutcome === 'Skipped') ? ' tsr-on' : ''
				}] : [],
				OutcomeChipSlot: (!tmpActive && tmpOutcome) ? [{ Outcome: tmpOutcome, ChipClass: ' tsr-chip-' + tmpOutcome.toLowerCase() }] : [],
				NotesEditSlot: tmpActive ? [{ ID: tmpID, Notes: tmpNotes }] : [],
				NotesViewSlot: (!tmpActive && tmpNotes) ? [{ ID: tmpID, ContainerID: 'TR-Notes-' + tmpID, Notes: tmpNotes }] : [],
				EvidenceUploadSlot: tmpActive ? [{ ID: tmpID }] : [],
				EvidenceWrapSlot: (tmpEvidenceItems.length || tmpActive) ? [{ ID: tmpID, Items: tmpEvidenceItems }] : []
			});
		});

		tmpState.Progress =
		{
			Done: tmpDone,
			Total: tmpState.Steps.length,
			Pct: tmpState.Steps.length ? Math.round((tmpDone / tmpState.Steps.length) * 100) : 0
		};
		tmpState.ActiveBarSlot = tmpActive ? [{}] : [];
		tmpState.DoneBarSlot = tmpActive ? [] : [{ Status: tmpRun.Status || '', ChipClass: ' tsr-chip-' + String(tmpRun.Status || '').toLowerCase().replace(/[^a-z]+/g, '-') }];
		tmpState.SummarySlot = (!tmpActive && String(tmpRun.Summary || '').trim()) ? [{ Summary: String(tmpRun.Summary) }] : [];
	}

	// ── outcomes (optimistic) ────────────────────────────────────────────────
	setOutcome(pID, pOutcome)
	{
		let tmpStep = this._rawStep(pID);
		if (!tmpStep || !this._active()) { return; }
		let tmpPrior = tmpStep.Outcome;
		// Clicking the selected outcome clears it back to pending.
		let tmpNext = (tmpStep.Outcome === pOutcome) ? '' : pOutcome;
		tmpStep.Outcome = tmpNext;
		this._rebuild();
		this.render();
		this._stepAPI().update(pID, { Outcome: tmpNext })
			.catch((pErr) =>
			{
				tmpStep.Outcome = tmpPrior;
				this._rebuild();
				this.render();
				this._toast('Could not save the outcome: ' + (pErr.message || pErr), 'error');
			});
	}

	setNotes(pID, pValue) { let tmpStep = this._rawStep(pID); if (tmpStep) { tmpStep.Notes = pValue; } }

	saveNotes(pID)
	{
		let tmpStep = this._rawStep(pID);
		if (!tmpStep) { return; }
		this._stepAPI().update(pID, { Notes: tmpStep.Notes || '' })
			.catch((pErr) => this._toast('Notes save failed: ' + (pErr.message || pErr), 'error'));
	}

	// ── evidence ─────────────────────────────────────────────────────────────
	uploadEvidence(pID, pFiles)
	{
		let tmpFiles = Array.prototype.slice.call(pFiles || []);
		if (!tmpFiles.length) { return; }
		this._toast('Uploading ' + tmpFiles.length + ' file' + (tmpFiles.length > 1 ? 's' : '') + '…', 'info');
		Promise.all(tmpFiles.map((pFile) => this._mediaAPI().upload('TestRunStep', pID, pFile)))
			.then(() => { this._toast('Uploaded.', 'success'); this._loadEvidenceFor(pID); })
			.catch((pErr) => this._toast('Upload failed: ' + (pErr.message || pErr), 'error'));
	}

	dropEvidence(pEvent, pID)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
		let tmpFiles = (pEvent && pEvent.dataTransfer && pEvent.dataTransfer.files) || [];
		if (tmpFiles.length) { this.uploadEvidence(pID, tmpFiles); }
	}

	allowDrop(pEvent) { if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); } }

	removeEvidence(pStepID, pIDMedia)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpDo = () => this._mediaAPI().remove(pIDMedia)
			.then(() => this._loadEvidenceFor(pStepID))
			.catch((pErr) => this._toast('Remove failed: ' + (pErr.message || pErr), 'error'));
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Remove this evidence?', { dangerous: true }).then((pOk) => { if (pOk) tmpDo(); });
		}
		else { tmpDo(); }
	}

	// ── finishing ────────────────────────────────────────────────────────────
	finish()
	{
		if (!this._active()) { return; }
		let tmpCounts = { Passed: 0, Failed: 0, Skipped: 0, Pending: 0 };
		this._steps.forEach((pStep) =>
		{
			let tmpOutcome = String(pStep.Outcome || '');
			if (!tmpOutcome) { tmpCounts.Pending++; }
			else if (typeof tmpCounts[tmpOutcome] === 'number') { tmpCounts[tmpOutcome]++; }
		});
		let tmpSuggested = (tmpCounts.Failed > 0) ? 'Failed' : 'Passed';
		let tmpParts = [tmpCounts.Passed + ' passed', tmpCounts.Failed + ' failed', tmpCounts.Skipped + ' skipped'];
		if (tmpCounts.Pending) { tmpParts.push(tmpCounts.Pending + ' not marked'); }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function')
		{
			return this._close(tmpSuggested);
		}
		tmpModal.show(
			{
				title: 'Finish this run?',
				content: '<p>' + tmpParts.join(', ') + '.</p><p>Suggested result: <strong>' + tmpSuggested + '</strong></p>',
				buttons:
				[
					{ Hash: 'Passed', Label: 'Passed', Style: (tmpSuggested === 'Passed') ? 'primary' : '' },
					{ Hash: 'Failed', Label: 'Failed', Style: (tmpSuggested === 'Failed') ? 'danger' : '' },
					{ Hash: 'Abandoned', Label: 'Abandoned', Style: '' },
					{ Hash: 'keep', Label: 'Keep testing', Style: '' }
				]
			})
			.then((pHash) =>
			{
				if (pHash === 'Passed' || pHash === 'Failed' || pHash === 'Abandoned') { this._close(pHash); }
			});
	}

	_close(pStatus)
	{
		this._runAPI().update(this._RunID, { Status: pStatus, CompletedDate: new Date().toISOString() })
			.then(() => { this._toast('Run marked ' + pStatus + '.', 'success'); this._load(); })
			.catch((pErr) => this._toast('Could not finish the run: ' + (pErr.message || pErr), 'error'));
	}

	leaveFeedback()
	{
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp.showNewFeedback !== 'function') { return; }
		tmpApp.showNewFeedback(
			{
				SourceType: 'Test Run',
				IDTestRun: this._RunID,
				Title: 'Feedback: ' + ((this._Run && this._Run.Name) || ('run #' + this._RunID))
			});
	}

	// ── navigation ───────────────────────────────────────────────────────────
	back()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showSection === 'function') { tmpApp.showSection('TestRun'); }
	}

	openRoutine()
	{
		let tmpApp = this.pict.PictApplication;
		let tmpID = (this._Run && this._Run.IDRoutine) || 0;
		if (tmpID && tmpApp && typeof tmpApp.showEntityDetail === 'function') { tmpApp.showEntityDetail('Routine', tmpID); }
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}

	// Mount the shared comments section under the run (OwnerType 'TestRun'); same
	// guard pattern as EntityDetail._mountComments.
	_mountComments()
	{
		let tmpView = this.pict.views['Plansheet-Comments'];
		if (!tmpView || typeof document === 'undefined' || !this._RunID) { return; }
		let tmpApp = this.pict.PictApplication;
		let tmpEditable = !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
		let tmpUser = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		tmpView.options.CurrentUser = { Key: tmpUser.IDUser || 0, Name: tmpUser.DisplayName || tmpUser.Email || 'You', Avatar: tmpUser.AvatarURL || '' };
		tmpView.options.ReadOnly = !tmpEditable;
		let tmpHost = document.getElementById('Entity-Comments');
		if (tmpHost) { tmpHost.innerHTML = ''; }
		if (this._CommentsLoadedFor === this._RunID)
		{
			tmpView.render();
			return;
		}
		this._CommentsLoadedFor = this._RunID;
		tmpView.setContext({ OwnerType: 'TestRun', IDOwner: this._RunID });
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		if (typeof document !== 'undefined')
		{
			let tmpSteps = (this.pict.AppData.TestRunner && this.pict.AppData.TestRunner.Steps) || [];
			for (let i = 0; i < tmpSteps.length; i++)
			{
				let tmpHost = document.getElementById(tmpSteps[i].BodyContainerID);
				if (tmpHost)
				{
					libMarkdownRender.renderInto(this.pict.providers.Content, tmpHost, tmpSteps[i].Body,
						'<span class="ps-rel-empty">No body for this step.</span>');
				}
				let tmpNotesSlot = (tmpSteps[i].NotesViewSlot && tmpSteps[i].NotesViewSlot[0]) || null;
				if (tmpNotesSlot)
				{
					let tmpNotesHost = document.getElementById(tmpNotesSlot.ContainerID);
					if (tmpNotesHost) { libMarkdownRender.renderInto(this.pict.providers.Content, tmpNotesHost, tmpNotesSlot.Notes, ''); }
				}
			}
			this._mountComments();
			// Prompts attached to this test run: the refinement cycle's evidence
			// surface (runs recorded here tie back to the run via IDTestRun).
			let tmpPromptsView = this.pict.views['Plansheet-PromptAttachments'];
			if (tmpPromptsView && this._RunID) { tmpPromptsView.showFor('TestRun', this._RunID, 'TestRun-Prompts'); }
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewTestRunner;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-TestRunner',
	DefaultRenderable: 'TestRunner-Display',
	AutoRender: false,
	CSS: /*css*/`
		.tsr { padding: 24px; max-width: 860px; margin: 0 auto; }
		.tsr-head { display: flex; align-items: center; gap: 12px; margin-bottom: 6px; }
		.tsr-head h2 { font-size: 20px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); margin: 0; flex: 1; }
		.tsr-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; color: var(--theme-color-text-secondary, #5b6376); font-size: 13px; }
		.tsr-progress { flex: 1; min-width: 160px; display: flex; align-items: center; gap: 8px; }
		.tsr-progress-track { flex: 1; height: 8px; border-radius: 999px; background: var(--theme-color-background-secondary, #eef1f6); overflow: hidden; }
		.tsr-progress-fill { height: 100%; border-radius: 999px; background: var(--theme-color-brand-primary, #2988df); transition: width 0.2s; }
		.tsr-card {
			background: var(--theme-color-background-panel, #fff);
			border: 1px solid var(--theme-color-border-default, #e6e9ee);
			border-left: 4px solid var(--theme-color-border-default, #e6e9ee);
			border-radius: 10px; padding: 14px 16px; margin-bottom: 12px;
		}
		.tsr-card-passed { border-left-color: var(--theme-color-status-success, #2e9e5b); }
		.tsr-card-failed { border-left-color: var(--theme-color-status-danger, #c0392b); }
		.tsr-card-skipped { border-left-color: var(--theme-color-text-secondary, #8a93a6); }
		.tsr-step-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
		.tsr-step-title { flex: 1; font-size: 15px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); }
		.tsr-body { margin-bottom: 8px; }
		.tsr-body img, .tsr-body video { max-width: 100%; border-radius: 6px; }
		.tsr-expected { margin-bottom: 10px; }
		.tsr-expected-label { display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 4px; }
		.tsr-expected-text {
			white-space: pre-wrap; word-break: break-word;
			font-size: 13px; line-height: 1.5; color: var(--theme-color-text-primary, #2c3140);
			background: var(--theme-color-background-secondary, #f6f8fa);
			border-left: 3px solid var(--theme-color-brand-primary, #2988df);
			border-radius: 6px; padding: 8px 12px;
		}
		.tsr-outcomes { display: flex; gap: 8px; margin: 10px 0; }
		.tsr-btn {
			flex: 0 0 auto; padding: 9px 18px; border-radius: 8px; font-size: 14px; cursor: pointer;
			border: 1px solid var(--theme-color-border-default, #d8dde6);
			background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140);
		}
		.tsr-btn-pass.tsr-on { background: var(--theme-color-status-success, #2e9e5b); color: #fff; border-color: transparent; }
		.tsr-btn-fail.tsr-on { background: var(--theme-color-status-danger, #c0392b); color: #fff; border-color: transparent; }
		.tsr-btn-skip.tsr-on { background: var(--theme-color-text-secondary, #8a93a6); color: #fff; border-color: transparent; }
		.tsr-chip { display: inline-block; padding: 3px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; color: #fff; background: var(--theme-color-text-secondary, #8a93a6); }
		.tsr-chip-passed { background: var(--theme-color-status-success, #2e9e5b); }
		.tsr-chip-failed { background: var(--theme-color-status-danger, #c0392b); }
		.tsr-chip-abandoned { background: var(--theme-color-text-secondary, #8a93a6); }
		.tsr-chip-in-progress { background: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-text-on-brand, #fff); }
		.tsr-notes { width: 100%; min-height: 56px; resize: vertical; box-sizing: border-box; }
		.tsr-evidence { margin-top: 10px; border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 8px; padding: 10px; }
		.tsr-evidence-label { font-size: 12px; font-weight: 600; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 8px; }
		.tsr-ref-label { font-size: 12px; font-weight: 600; color: var(--theme-color-text-secondary, #5b6376); margin: 8px 0 6px; }
		.tsr-bar {
			position: sticky; bottom: 0; display: flex; align-items: center; gap: 12px;
			background: var(--theme-color-background-panel, #fff);
			border: 1px solid var(--theme-color-border-default, #e6e9ee);
			border-radius: 10px; padding: 12px 16px; margin-top: 16px;
			box-shadow: 0 -2px 12px rgba(0,0,0,0.06);
		}
		.tsr-bar-text { flex: 1; font-size: 14px; color: var(--theme-color-text-secondary, #5b6376); }
		.tsr-summary { background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 10px; padding: 12px 16px; margin-bottom: 14px; font-size: 14px; white-space: pre-wrap; }
		.tsr-comments { margin-top: 20px; }
	`,
	Templates:
	[
		{
			Hash: 'TestRunner-Display',
			Template: /*html*/`
<div class="tsr">
	<div class="tsr-head">
		<button class="ps-btn" onclick="_Pict.views['Plansheet-TestRunner'].back()">← Test Runs</button>
		<h2>{~D:AppData.TestRunner.HeaderTitle~}</h2>
		<span class="tsr-chip{~D:AppData.TestRunner.StatusChipClass~}">{~D:AppData.TestRunner.Status~}</span>
	</div>
	<div class="tsr-meta">
		<span class="ps-chip"><a class="ps-link" onclick="_Pict.views['Plansheet-TestRunner'].openRoutine()">{~D:AppData.TestRunner.RoutineName~}</a></span>
		{~TS:TR-Env:AppData.TestRunner.EnvironmentSlot~}
		<div class="tsr-progress">
			<span>{~D:AppData.TestRunner.Progress.Done~}/{~D:AppData.TestRunner.Progress.Total~}</span>
			<div class="tsr-progress-track"><div class="tsr-progress-fill" style="width: {~D:AppData.TestRunner.Progress.Pct~}%"></div></div>
		</div>
	</div>
	{~TS:TR-Summary:AppData.TestRunner.SummarySlot~}
	{~TS:TR-StepCard:AppData.TestRunner.Steps~}
	{~TS:TR-ActiveBar:AppData.TestRunner.ActiveBarSlot~}
	{~TS:TR-DoneBar:AppData.TestRunner.DoneBarSlot~}
	<div class="tsr-prompts"><h4 class="ps-detail-board-h">Prompts</h4><div id="TestRun-Prompts"></div></div>
	<div class="tsr-comments"><h4 class="ps-detail-board-h">Comments</h4><div id="Entity-Comments"></div></div>
</div>`
		},
		{ Hash: 'TR-Env', Template: /*html*/`<span class="ps-chip" title="Environment">{~D:Record.Environment~}</span>` },
		{ Hash: 'TR-Summary', Template: /*html*/`<div class="tsr-summary">{~D:Record.Summary~}</div>` },
		{
			Hash: 'TR-StepCard',
			Template: /*html*/`
<div class="tsr-card{~D:Record.CardClass~}">
	<div class="tsr-step-head">
		<span class="ps-step-num">{~D:Record.Idx~}</span>
		<span class="tsr-step-title">{~D:Record.Title~}</span>
		{~TS:TR-OutcomeChip:Record.OutcomeChipSlot~}
	</div>
	<div class="tsr-body" id="{~D:Record.BodyContainerID~}"></div>
	{~TS:TR-RefMedia:Record.RefMediaSlot~}
	{~TS:TR-Expected:Record.ExpectedSlot~}
	{~TS:TR-OutcomeButtons:Record.OutcomeButtonsSlot~}
	{~TS:TR-NotesEdit:Record.NotesEditSlot~}
	{~TS:TR-NotesView:Record.NotesViewSlot~}
	{~TS:TR-Evidence:Record.EvidenceWrapSlot~}
</div>`
		},
		{ Hash: 'TR-RefMedia', Template: /*html*/`<div class="tsr-ref-label">From the routine:</div><div class="ps-media-grid">{~TS:TR-MediaItem:Record.Items~}</div>` },
		{ Hash: 'TR-Expected', Template: /*html*/`<div class="tsr-expected"><span class="tsr-expected-label">Expect</span><div class="tsr-expected-text">{~D:Record.Expected~}</div></div>` },
		{
			Hash: 'TR-OutcomeButtons',
			Template: /*html*/`
<div class="tsr-outcomes">
	<button class="tsr-btn tsr-btn-pass{~D:Record.PassedClass~}" onclick="_Pict.views['Plansheet-TestRunner'].setOutcome({~D:Record.ID~},'Passed')">Worked</button>
	<button class="tsr-btn tsr-btn-fail{~D:Record.FailedClass~}" onclick="_Pict.views['Plansheet-TestRunner'].setOutcome({~D:Record.ID~},'Failed')">Didn't work</button>
	<button class="tsr-btn tsr-btn-skip{~D:Record.SkippedClass~}" onclick="_Pict.views['Plansheet-TestRunner'].setOutcome({~D:Record.ID~},'Skipped')">Skip</button>
</div>`
		},
		{ Hash: 'TR-OutcomeChip', Template: /*html*/`<span class="tsr-chip{~D:Record.ChipClass~}">{~D:Record.Outcome~}</span>` },
		{
			Hash: 'TR-NotesEdit',
			Template: /*html*/`<textarea class="ps-input tsr-notes" placeholder="Notes (markdown). What did you see?"
	oninput="_Pict.views['Plansheet-TestRunner'].setNotes({~D:Record.ID~}, this.value)"
	onchange="_Pict.views['Plansheet-TestRunner'].saveNotes({~D:Record.ID~})">{~D:Record.Notes~}</textarea>`
		},
		{ Hash: 'TR-NotesView', Template: /*html*/`<div class="tsr-ref-label">Notes:</div><div class="tsr-body" id="{~D:Record.ContainerID~}"></div>` },
		{
			Hash: 'TR-Evidence',
			Template: /*html*/`
<div class="tsr-evidence" ondragover="_Pict.views['Plansheet-TestRunner'].allowDrop(event)" ondrop="_Pict.views['Plansheet-TestRunner'].dropEvidence(event, {~D:Record.ID~})">
	<div class="tsr-evidence-label">Evidence</div>
	<div class="ps-media-grid">{~TS:TR-MediaItem:Record.Items~}</div>
	{~TS:TR-EvidenceUpload:Record.UploadSlot~}
</div>`
		},
		{ Hash: 'TR-EvidenceUpload', Template: /*html*/`<label class="ps-btn ps-btn-sm ps-file-btn">+ Add evidence<input type="file" multiple accept="image/*,video/*" style="display:none" onchange="_Pict.views['Plansheet-TestRunner'].uploadEvidence({~D:Record.ID~}, this.files)"></label>` },
		{ Hash: 'TR-MediaItem', Template: /*html*/`<div class="ps-media-item">{~TS:TR-MediaImage:Record.ImageSlot~}{~TS:TR-MediaVideo:Record.VideoSlot~}{~TS:TR-MediaFile:Record.FileSlot~}{~TS:TR-MediaRemove:Record.RemoveSlot~}</div>` },
		{ Hash: 'TR-MediaImage', Template: /*html*/`<a href="{~D:Record.URL~}" target="_blank" rel="noopener"><img class="ps-media-thumb" src="{~D:Record.URL~}" alt="{~D:Record.Title~}" title="{~D:Record.Title~}"></a>` },
		{ Hash: 'TR-MediaVideo', Template: /*html*/`<video class="ps-media-video" controls preload="metadata" src="{~D:Record.URL~}" title="{~D:Record.Title~}"></video>` },
		{ Hash: 'TR-MediaFile', Template: /*html*/`<a class="ps-link ps-media-file" href="{~D:Record.URL~}" target="_blank" rel="noopener"><span class="ps-media-file-type">{~D:Record.Type~}</span>{~D:Record.Title~}</a>` },
		{ Hash: 'TR-MediaRemove', Template: /*html*/`<button class="ps-chip-x ps-media-x" title="Remove" onclick="_Pict.views['Plansheet-TestRunner'].removeEvidence({~D:Record.StepID~},{~D:Record.ID~})">×</button>` },
		{
			Hash: 'TR-ActiveBar',
			Template: /*html*/`
<div class="tsr-bar">
	<span class="tsr-bar-text">Mark each step, then finish the run.</span>
	<button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-TestRunner'].finish()">Finish run</button>
</div>`
		},
		{
			Hash: 'TR-DoneBar',
			Template: /*html*/`
<div class="tsr-bar">
	<span class="tsr-bar-text">This run is finished: <span class="tsr-chip{~D:Record.ChipClass~}">{~D:Record.Status~}</span></span>
	<button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-TestRunner'].leaveFeedback()">Leave feedback</button>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'TestRunner-Display',
			TemplateHash: 'TestRunner-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
